import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { eventBus } from '@/events/eventBus';
import {
  generateDepositLink,
  simulateStripeWebhookPaid,
} from '@/domain/depositFlow';
import { advanceSimClock } from '@/domain/simClockFlow';
import type { Channel, ConversationEntry } from '@/domain/types';
import { isWithAgent } from '@atink/domain';

// Per-customer "sequence clock": a 3-attempt call/message cadence that ends in a
// red terminal state when a customer is never reached, UNLESS they are in the
// "money cohort" (deposit on file with no booking yet); those never go red and
// re-arm at stage 3 instead of advancing to red.
//
// Follows the existing domain flow pattern: "now" is always read from the
// simulation store's clockIso (never Date.now()), and every state change goes
// through brain-store actions.

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

export type SequenceStage = 1 | 2 | 3 | 'red';
export type RedKind = 'said-no' | 'never-reached';

/** Lane a queued customer sorts into. Pushed leads always enter as 'fresh'. */
export type SequenceCohort = 'fresh' | 'money' | 'open-business';

export interface SequenceState {
  stage: SequenceStage;
  redKind?: RedKind;
  lastAttemptAt?: string; // ISO
  nextDueAt?: string; // ISO - while in the future the item is "dormant"
  /** Lane this queued customer belongs to. Set when a lead is pushed to the queue. */
  cohort?: SequenceCohort;
  /**
   * The conversation this record was pushed from. Its presence marks a record as
   * "manually pushed", which lets buildStandardQueue mint a pushed-lead card even
   * when the customer has no other queue signal. Keyed by customerId, since a
   * conversation is identified by its customer in this prototype.
   */
  sourceConversationId?: string;
  /** Optional callback time chosen at push. Pins the card at its slot + rails it. */
  pinnedCallbackAt?: string; // ISO
  /** B8: when the callback was pinned — a contact before it never spends it. */
  pinnedAt?: string; // ISO
  /** One-line description captured at push; travels onto the pushed-lead card. */
  description?: string;
  /**
   * Deposit this pushed record is about (Prompt I1): set when an expired link on
   * a now-taken date is routed back to the queue, so the resend context + agent
   * attribution travel onto the money-cohort card.
   */
  depositId?: string;
}

function nowIso() {
  return useSimulationStore.getState().clockIso;
}

/**
 * The "money cohort never goes red" predicate. A customer is in the money cohort
 * when they have a deposit that has NOT converted to a booking: either a paid
 * deposit with no bookingId, or a deposit still awaiting payment (pending). These
 * customers keep cycling at stage 3 rather than terminating red.
 */
export function isMoneyCohortCustomer(customerId: string): boolean {
  const { deposits } = useBrainStore.getState();
  return Object.values(deposits).some((deposit) => {
    if (deposit.customerId !== customerId) return false;
    if (deposit.status === 'paid' && !deposit.bookingId) return true;
    if (deposit.status === 'pending') return true;
    return false;
  });
}

/**
 * The customer was just contacted (call completed or message sent). Stamps the
 * attempt time and arms the 24h window. Stage advances on the clock passing that
 * window (see reconcileSequenceClock), not here. No-op once the sequence is red.
 */
export function recordContactAttempt(customerId: string): void {
  const brain = useBrainStore.getState();
  const existing = brain.sequenceState[customerId];
  if (existing?.stage === 'red') return;
  const now = nowIso();
  const nextDueAt = new Date(new Date(now).getTime() + TWENTY_FOUR_HOURS_MS).toISOString();
  if (!existing) {
    brain.setSequenceState(customerId, { stage: 1, lastAttemptAt: now, nextDueAt });
    return;
  }
  // A callback pinned at push is spent once the Agent makes contact inside (or
  // after) its window — it must not sit on the Scheduled rail forever (B8 / D05-04).
  const pinSpent =
    existing.pinnedCallbackAt != null &&
    new Date(now).getTime() >= new Date(existing.pinnedCallbackAt).getTime() - 15 * 60 * 1000 &&
    (!existing.pinnedAt || new Date(now).getTime() > new Date(existing.pinnedAt).getTime());
  brain.patchSequenceState(customerId, {
    lastAttemptAt: now,
    nextDueAt,
    ...(pinSpent ? { pinnedCallbackAt: undefined, pinnedAt: undefined } : {}),
  });
}

/**
 * The customer replied. Reset the sequence to a fresh stage 1, clearing any red
 * state, timers and stamps, but PRESERVING push metadata (cohort,
 * sourceConversationId, description, pinnedCallbackAt) so a pushed lead survives
 * the reset. No-op for a customer with no sequence record — only queued people
 * get reordered on reply. The queue orders by priority, so "jumps to the top of
 * active" is expressed there; here we only reset the sequence.
 */
export function recordInboundReply(customerId: string): void {
  const brain = useBrainStore.getState();
  if (!brain.sequenceState[customerId]) return;
  brain.patchSequenceState(customerId, {
    stage: 1,
    redKind: undefined,
    lastAttemptAt: undefined,
    nextDueAt: undefined,
  });
}

export interface PushToQueueInput {
  customerId: string;
  /** One-line, optional at push; stored on the sequence record when provided. */
  description?: string;
  /** Captured phone (may be blank). Persisted to the customer only if they had none. */
  phone?: string;
  /** Optional callback time (ISO) chosen at push. */
  pinnedCallbackAt?: string;
}

/**
 * Hand a conversation lead to a booking agent. The queue is derived, so a
 * sequence record carrying a sourceConversationId is all it takes for a
 * pushed-lead card to appear in MyQ (see buildStandardQueue).
 *
 * A customer may already own a stage-1 record from an earlier contact attempt or
 * confirmation flow that has NO sourceConversationId; buildStandardQueue skips
 * such records, so a naive "record exists -> bail" would make the push silently
 * vanish. Three cases:
 *  - Record already pushed (has a sourceConversationId): genuine no-op, return
 *    false without overwriting the in-flight push.
 *  - Record exists but was never pushed (no sourceConversationId): patch it to
 *    add the push metadata + source WITHOUT resetting its stage, so it becomes a
 *    queue-visible pushed lead.
 *  - No record: mint a fresh stage-1 pushed record.
 * If a phone was captured and the customer had none on file, persist it (both
 * success paths) so the pushed card can offer Call.
 */
export function pushToQueue(input: PushToQueueInput): boolean {
  const brain = useBrainStore.getState();
  const existing = brain.sequenceState[input.customerId];
  const customer = brain.customers[input.customerId];
  // Already pushed: never overwrite an in-flight push. Anas 8 Oct: once pushed
  // (red or booked included) the lead is the Agent's for good (shared rule).
  if (isWithAgent(customer, existing)) return false;

  const phone = input.phone?.trim();
  if (phone && customer && !customer.phone) {
    brain.upsertCustomer({ ...customer, phone });
  }
  const description = input.description?.trim();

  if (existing) {
    // Patch a never-pushed record in place: add source + push metadata, keep stage.
    // Clear stale cadence state (mirrors recordInboundReply) so the fresh pushed
    // lead lands ACTIVE — a leftover red flag or a future nextDueAt would otherwise
    // make it read as red or sink it into the dormant "Completed this sequence" set.
    brain.patchSequenceState(input.customerId, {
      cohort: 'fresh',
      sourceConversationId: input.customerId,
      redKind: undefined,
      nextDueAt: undefined,
      ...(description ? { description } : {}),
      ...(input.pinnedCallbackAt ? { pinnedCallbackAt: input.pinnedCallbackAt } : {}),
    });
  } else {
    brain.setSequenceState(input.customerId, {
      stage: 1,
      cohort: 'fresh',
      sourceConversationId: input.customerId,
      description: description || undefined,
      pinnedCallbackAt: input.pinnedCallbackAt,
    });
  }

  const name = customer ? `${customer.firstName} ${customer.lastName}` : 'Lead';
  brain.appendActivity({
    id: `act-push-${input.customerId}-${Date.now()}`,
    type: 'pushed-to-queue',
    customerId: input.customerId,
    body: `${name} pushed to the booking queue.${description ? ` ${description}` : ''}`,
    timestamp: nowIso(),
  });
  eventBus.publish('sequence:pushed', { customerId: input.customerId });
  return true;
}

/**
 * Advance every sequence whose 24h window has elapsed. Stages 1 and 2 step up one
 * and re-activate. Stage 3 re-activates and cycles when the customer is in the
 * money cohort; otherwise it terminates red ('never-reached').
 */
export function reconcileSequenceClock(): void {
  const brain = useBrainStore.getState();
  const nowMs = new Date(nowIso()).getTime();
  for (const [customerId, state] of Object.entries(brain.sequenceState)) {
    if (state.stage === 'red') continue;
    if (!state.nextDueAt) continue;
    if (new Date(state.nextDueAt).getTime() > nowMs) continue;
    if (state.stage === 1 || state.stage === 2) {
      brain.patchSequenceState(customerId, {
        stage: (state.stage + 1) as SequenceStage,
        nextDueAt: undefined,
      });
    } else if (isMoneyCohortCustomer(customerId)) {
      // Money-cohort customers never go red; at stage 3 they re-arm on the next
      // contact attempt instead of advancing to red.
      brain.patchSequenceState(customerId, { stage: 3, nextDueAt: undefined });
    } else {
      brain.patchSequenceState(customerId, {
        stage: 'red',
        redKind: 'never-reached',
        nextDueAt: undefined,
      });
    }
  }
}

/**
 * Advance the simulation clock by `hours`, then reconcile every sequence against
 * the new "now". The demo control for fast-forwarding the cadence.
 */
export function advanceSequenceClock(hours: number): void {
  // The ONE day-advanced hook (tv-2na.10.13): moves the clock forward (canonical
  // +10:00 form) and runs the sequence clock, link expiry, top-up schedule and
  // T-4d waiver dispatch — server-side in the served build.
  void advanceSimClock({ hours });
}

/**
 * The customer said no. Terminate the sequence red ('said-no'), KEEPING the push
 * metadata (cohort, sourceConversationId, description): the declined lead stays
 * on the Agent's list as a dark red, counted under "Declined" (B8 / N13). A pin
 * is dropped: nobody calls a client who said no.
 */
export function markSaidNo(customerId: string): void {
  const brain = useBrainStore.getState();
  const patch: Partial<SequenceState> = {
    stage: 'red',
    redKind: 'said-no',
    nextDueAt: undefined,
    pinnedCallbackAt: undefined,
    pinnedAt: undefined,
  };
  if (brain.sequenceState[customerId]) brain.patchSequenceState(customerId, patch);
  else brain.setSequenceState(customerId, { stage: 'red', redKind: 'said-no' });
}

/**
 * "Call back at…" from the hang-up sheet (B8 / 75nf + 75mr): pins the callback on
 * the lead's sequence record, so it rails now and jumps to the top at its time.
 */
export function scheduleCallback(customerId: string, atIso: string): void {
  const brain = useBrainStore.getState();
  const pin = { pinnedCallbackAt: atIso, pinnedAt: nowIso() };
  if (brain.sequenceState[customerId]) brain.patchSequenceState(customerId, pin);
  else brain.setSequenceState(customerId, { stage: 1, ...pin });
}

/**
 * Demo trigger: fake an inbound reply from the customer. Appends an inbound
 * conversation entry (reusing their most recent channel, else SMS) and resets
 * their sequence to stage 1.
 */
export function simulateInboundReply(customerId: string): void {
  const brain = useBrainStore.getState();
  const customer = brain.customers[customerId];
  const lastChannel: Channel =
    customer && customer.conversation.length > 0
      ? customer.conversation[customer.conversation.length - 1]!.channel
      : 'sms';
  const entry: ConversationEntry = {
    id: `convo-inbound-reply-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    customerId,
    channel: lastChannel,
    direction: 'in',
    body: "Sounds good, let's lock it in",
    timestamp: nowIso(),
    authorRole: 'customer',
  };
  brain.addConversationEntry(customerId, entry);
  recordInboundReply(customerId);
}

/**
 * Demo trigger: fire a scheduled callback now. `itemId` is a queue item id of the
 * form `q-frs-<signalId>` (strip that prefix; if absent, treat the whole string as
 * the signal id). Pulls the matching future-readiness signal's `scheduledFor` to
 * now so it enters the 15-min firing window (becomes callback-firing -> scheduledDue,
 * top of the active tab). The operator queue reads signals off the customer record,
 * so we replace the signal in place there via upsertCustomer, and keep the global
 * futureSignals map consistent via the existing upsertFutureSignal. No-op if the
 * signal is not found.
 */
export function fireScheduledCallback(itemId: string): void {
  const prefix = 'q-frs-';
  const signalId = itemId.startsWith(prefix) ? itemId.slice(prefix.length) : itemId;
  const brain = useBrainStore.getState();
  const now = nowIso();
  for (const customer of Object.values(brain.customers)) {
    const idx = customer.futureReadinessSignals.findIndex((s) => s.id === signalId);
    if (idx === -1) continue;
    const updated = {
      ...customer.futureReadinessSignals[idx]!,
      scheduledFor: now,
      status: 'scheduled' as const,
    };
    const nextSignals = [...customer.futureReadinessSignals];
    nextSignals[idx] = updated;
    brain.upsertCustomer({ ...customer, futureReadinessSignals: nextSignals });
    brain.upsertFutureSignal(updated);
    return;
  }
}

/**
 * Demo trigger: simulate a fresh deposit being paid for a customer so a paid,
 * unbooked deposit surfaces in the queue as a deposit-on-file (money) item.
 * Reuses depositFlow: generate a Stripe link, then fire the paid webhook with no
 * pre-picked slot, which marks the deposit paid (paidAt = now, no bookingId) ->
 * a deposit-on-file item. No-op if the customer is unknown.
 */
export function simulateDepositPaid(customerId: string): void {
  const brain = useBrainStore.getState();
  const customer = brain.customers[customerId];
  // D05-11: no deposit is taken without the client's email on file.
  if (!customer || !(customer.email ?? '').trim()) return;
  const artistId =
    Object.values(brain.artists).find((a) => a.studioId === customer.primaryStudioId)?.id ?? 'pat';
  const deposit = generateDepositLink({
    customerId,
    studioId: customer.primaryStudioId,
    artistId,
    amountAud: 400,
    channel: 'sms',
    sessionLength: 'half-day',
    source: 'agent-initiated-chat',
  });
  simulateStripeWebhookPaid({ depositId: deposit.id, newOnFileWithoutDate: true });
}

// A completed call counts as a contact attempt (callFlow.endCall publishes this
// with { customerId }). Registered at module scope so importing this module for
// its side effect wires the subscription at app start.
eventBus.subscribe<{ customerId: string }>('call:completed', (payload) => {
  recordContactAttempt(payload.customerId);
});
